import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Shield, 
  Lock, 
  Wallet, 
  Server, 
  TrendingUp, 
  CheckCircle2, 
  ArrowDown,
  ChevronRight,
  Zap,
  Eye,
  Ban,
  RefreshCw,
  PiggyBank,
  BarChart3
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

// Performance data
const monthlyPerformance = [
  { month: 'Jun', return: 4.2 },
  { month: 'Jul', return: 5.8 },
  { month: 'Aug', return: 3.9 },
  { month: 'Sep', return: 6.2 },
  { month: 'Oct', return: 4.7 },
  { month: 'Nov', return: 5.1 },
  { month: 'Dec', return: 5.4 },
];

const cumulativeGrowth = [
  { month: 'Start', value: 100000 },
  { month: 'M1', value: 104200 },
  { month: 'M2', value: 110243 },
  { month: 'M3', value: 114542 },
  { month: 'M4', value: 121644 },
  { month: 'M5', value: 127361 },
  { month: 'M6', value: 133855 },
  { month: 'M7', value: 141084 },
];

const HowItWorksPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef({});

  // Intersection observer for animations
  useEffect(() => {
    const observers = {};
    const sections = ['hero', 'steps', 'security', 'performance', 'cta'];
    
    sections.forEach(section => {
      observers[section] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [section]: true }));
          }
        },
        { threshold: 0.1, rootMargin: '50px' }
      );
      
      if (sectionRefs.current[section]) {
        observers[section].observe(sectionRefs.current[section]);
      }
    });

    return () => {
      sections.forEach(section => {
        if (observers[section]) observers[section].disconnect();
      });
    };
  }, []);

  // Auto-advance steps
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const steps = [
    {
      id: 1,
      icon: Wallet,
      title: "Fund Your Exchange",
      subtitle: "Keep Full Control",
      description: "Deposit your assets into your preferred exchange account. Your funds stay in YOUR wallet on YOUR exchange. We never hold your assets.",
      highlight: "100% Asset Custody Retained",
      color: "amber"
    },
    {
      id: 2,
      icon: Lock,
      title: "Grant API Access",
      subtitle: "Trade-Only Permission",
      description: "Provide us with API keys that have ONLY trading permissions. No withdrawal access means your assets are completely protected from any external transfers.",
      highlight: "Zero Withdrawal Risk",
      color: "green"
    },
    {
      id: 3,
      icon: Server,
      title: "Connect to Our System",
      subtitle: "Master Trader Integration",
      description: "Our advanced master trader server connects to your API. Our algorithmic strategies and expert traders manage your portfolio across crypto and gold markets 24/7 with institutional-grade execution.",
      highlight: "24/7 Automated Trading",
      color: "blue"
    },
    {
      id: 4,
      icon: TrendingUp,
      title: "Grow & Withdraw",
      subtitle: "Your Profits, Your Control",
      description: "Watch your portfolio grow with our proven strategies. Withdraw your capital or profits anytime directly from your exchange - no middlemen, no delays.",
      highlight: "Instant Access to Funds",
      color: "purple"
    }
  ];

  const securityFeatures = [
    { icon: Ban, title: "No Withdrawal Access", desc: "API keys are trade-only. We cannot move your funds." },
    { icon: Eye, title: "Full Transparency", desc: "Monitor all trades in real-time on your exchange." },
    { icon: Shield, title: "Your Keys, Your Coins", desc: "Assets never leave your personal exchange wallet." },
    { icon: RefreshCw, title: "Revoke Anytime", desc: "Disable API access instantly whenever you want." },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-black/95 border border-white/10 px-3 py-2 rounded-lg">
          <p className="text-white/60 text-xs">{label}</p>
          <p className="text-amber-400 text-sm font-medium">
            {payload[0].dataKey === 'return' ? `+${payload[0].value}%` : `$${payload[0].value.toLocaleString()}`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      {/* Hero Section */}
      <section 
        ref={el => sectionRefs.current.hero = el}
        className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden"
      >
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-amber-600/5 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8">
          <div 
            className="text-center max-w-3xl mx-auto"
            style={{
              opacity: isVisible.hero ? 1 : 0,
              transform: isVisible.hero ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s ease-out'
            }}
          >
            {/* Breadcrumb */}
            <div className="flex items-center justify-center gap-2 text-white/40 text-xs mb-8">
              <Link to="/" className="hover:text-white/60 transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-white/60">How It Works</span>
            </div>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-light text-white mb-6 tracking-tight">
              How <span className="brand-font text-amber-400">COIN BILLS</span> Works
            </h1>
            <p className="text-white/50 text-base md:text-lg font-light leading-relaxed mb-8">
              A secure, transparent, and fully non-custodial approach to professional crypto and gold asset management. 
              Your assets stay in your control—always.
            </p>

            {/* Trust badges */}
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              {[
                { icon: Shield, text: "Non-Custodial" },
                { icon: Lock, text: "Trade-Only API" },
                { icon: Zap, text: "24/7 Automation" }
              ].map((badge, i) => (
                <div 
                  key={badge.text}
                  className="flex items-center gap-2 px-4 py-2 bg-white/[0.03] border border-white/[0.08] rounded-full"
                  style={{
                    opacity: isVisible.hero ? 1 : 0,
                    transform: isVisible.hero ? 'translateY(0)' : 'translateY(20px)',
                    transition: `all 0.6s ease-out ${200 + i * 100}ms`
                  }}
                >
                  <badge.icon className="w-4 h-4 text-amber-500" />
                  <span className="text-white/70 text-xs font-medium">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ArrowDown className="w-5 h-5 text-white/30" />
        </div>
      </section>

      {/* Steps Flowchart Section */}
      <section 
        ref={el => sectionRefs.current.steps = el}
        className="py-16 md:py-24 relative"
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          {/* Section Header */}
          <div 
            className="text-center mb-12 md:mb-16"
            style={{
              opacity: isVisible.steps ? 1 : 0,
              transform: isVisible.steps ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.7s ease-out'
            }}
          >
            <p className="text-amber-500/80 text-xs font-medium tracking-[0.3em] mb-4">THE PROCESS</p>
            <h2 className="text-2xl md:text-4xl font-light text-white mb-4">Four Simple Steps</h2>
            <p className="text-white/40 text-sm md:text-base max-w-xl mx-auto">
              From onboarding to profit—here's how we help you grow your assets while maintaining complete security.
            </p>
          </div>

          {/* Desktop Flowchart */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Connection Line */}
              <div className="absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              
              {/* Steps */}
              <div className="grid grid-cols-4 gap-6">
                {steps.map((step, index) => (
                  <div 
                    key={step.id}
                    className={`relative cursor-pointer transition-all duration-500 ${
                      activeStep === index ? 'scale-105' : 'scale-100 opacity-70 hover:opacity-100'
                    }`}
                    onClick={() => setActiveStep(index)}
                    style={{
                      opacity: isVisible.steps ? (activeStep === index ? 1 : 0.7) : 0,
                      transform: isVisible.steps ? 'translateY(0)' : 'translateY(40px)',
                      transition: `all 0.6s ease-out ${index * 150}ms`
                    }}
                  >
                    {/* Step Number */}
                    <div className="flex justify-center mb-6">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-medium transition-all duration-500 ${
                        activeStep === index 
                          ? 'bg-amber-500 text-black' 
                          : 'bg-white/[0.05] text-white/40 border border-white/10'
                      }`}>
                        {step.id}
                      </div>
                    </div>

                    {/* Card */}
                    <div className={`bg-white/[0.02] border rounded-2xl p-6 transition-all duration-500 ${
                      activeStep === index 
                        ? 'border-amber-500/30 bg-amber-500/[0.03]' 
                        : 'border-white/[0.06] hover:border-white/10'
                    }`}>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-500 ${
                        activeStep === index ? 'bg-amber-500/20' : 'bg-white/[0.05]'
                      }`}>
                        <step.icon className={`w-6 h-6 transition-colors duration-500 ${
                          activeStep === index ? 'text-amber-400' : 'text-white/40'
                        }`} />
                      </div>
                      
                      <h3 className="text-white text-lg font-medium mb-1">{step.title}</h3>
                      <p className="text-amber-500/70 text-xs font-medium tracking-wide mb-3">{step.subtitle}</p>
                      <p className="text-white/40 text-sm leading-relaxed mb-4">{step.description}</p>
                      
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-500 ${
                        activeStep === index 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-white/[0.05] text-white/50'
                      }`}>
                        <CheckCircle2 className="w-3 h-3" />
                        {step.highlight}
                      </div>
                    </div>

                    {/* Arrow */}
                    {index < 3 && (
                      <div className="absolute top-24 -right-3 transform translate-x-1/2">
                        <ArrowRight className={`w-6 h-6 transition-colors duration-500 ${
                          activeStep > index ? 'text-amber-500' : 'text-white/20'
                        }`} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile Steps */}
          <div className="lg:hidden space-y-4">
            {steps.map((step, index) => (
              <div 
                key={step.id}
                className="relative"
                style={{
                  opacity: isVisible.steps ? 1 : 0,
                  transform: isVisible.steps ? 'translateX(0)' : 'translateX(-30px)',
                  transition: `all 0.6s ease-out ${index * 150}ms`
                }}
              >
                {/* Connection Line */}
                {index < 3 && (
                  <div className="absolute left-6 top-14 bottom-0 w-0.5 bg-gradient-to-b from-amber-500/30 to-transparent" />
                )}

                <div className="flex gap-4">
                  {/* Step Number */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-amber-500 text-black flex items-center justify-center text-lg font-medium">
                      {step.id}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                        <step.icon className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <h3 className="text-white text-base font-medium">{step.title}</h3>
                        <p className="text-amber-500/70 text-[10px] font-medium tracking-wide">{step.subtitle}</p>
                      </div>
                    </div>
                    <p className="text-white/40 text-sm leading-relaxed mb-3">{step.description}</p>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-500/10 rounded-full text-green-400 text-[10px] font-medium">
                      <CheckCircle2 className="w-3 h-3" />
                      {step.highlight}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section 
        ref={el => sectionRefs.current.security = el}
        className="py-16 md:py-24 bg-gradient-to-b from-transparent via-amber-500/[0.02] to-transparent"
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div 
            className="text-center mb-12"
            style={{
              opacity: isVisible.security ? 1 : 0,
              transform: isVisible.security ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.7s ease-out'
            }}
          >
            <p className="text-amber-500/80 text-xs font-medium tracking-[0.3em] mb-4">YOUR PROTECTION</p>
            <h2 className="text-2xl md:text-4xl font-light text-white mb-4">Complete Asset Safety</h2>
            <p className="text-white/40 text-sm md:text-base max-w-xl mx-auto">
              Our non-custodial model ensures you maintain 100% control over your assets at all times.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {securityFeatures.map((feature, index) => (
              <div 
                key={feature.title}
                className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6 hover:border-amber-500/20 transition-all duration-500 group"
                style={{
                  opacity: isVisible.security ? 1 : 0,
                  transform: isVisible.security ? 'translateY(0)' : 'translateY(30px)',
                  transition: `all 0.6s ease-out ${index * 100}ms`
                }}
              >
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4 group-hover:bg-amber-500/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="text-white font-medium mb-2">{feature.title}</h3>
                <p className="text-white/40 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>

          {/* Visual Diagram */}
          <div 
            className="mt-12 md:mt-16 bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 md:p-10"
            style={{
              opacity: isVisible.security ? 1 : 0,
              transform: isVisible.security ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.7s ease-out 400ms'
            }}
          >
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10">
              {/* Your Exchange */}
              <div className="text-center">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-green-500/10 border border-green-500/20 flex items-center justify-center mb-3 mx-auto">
                  <Wallet className="w-10 h-10 md:w-12 md:h-12 text-green-400" />
                </div>
                <p className="text-white font-medium text-sm md:text-base">Your Exchange</p>
                <p className="text-green-400 text-xs">Assets Stay Here</p>
              </div>

              {/* API Connection */}
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-2 text-white/30">
                  <div className="w-8 md:w-16 h-0.5 bg-gradient-to-r from-green-500/50 to-amber-500/50" />
                  <Lock className="w-5 h-5 text-amber-500" />
                  <div className="w-8 md:w-16 h-0.5 bg-gradient-to-r from-amber-500/50 to-green-500/50" />
                </div>
                <p className="text-white/40 text-[10px] mt-2">Trade-Only API</p>
              </div>

              {/* Coin Bills System */}
              <div className="text-center">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-3 mx-auto">
                  <Server className="w-10 h-10 md:w-12 md:h-12 text-amber-400" />
                </div>
                <p className="text-white font-medium text-sm md:text-base">Coin Bills</p>
                <p className="text-amber-400 text-xs">Trading Signals</p>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-white/[0.06] text-center">
              <p className="text-white/30 text-xs md:text-sm">
                <span className="text-red-400">✗</span> We CANNOT withdraw your funds &nbsp;|&nbsp; 
                <span className="text-green-400">✓</span> We CAN execute trades on your behalf
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Performance Section */}
      <section 
        ref={el => sectionRefs.current.performance = el}
        className="py-16 md:py-24"
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div 
            className="text-center mb-12"
            style={{
              opacity: isVisible.performance ? 1 : 0,
              transform: isVisible.performance ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.7s ease-out'
            }}
          >
            <p className="text-amber-500/80 text-xs font-medium tracking-[0.3em] mb-4">TRACK RECORD</p>
            <h2 className="text-2xl md:text-4xl font-light text-white mb-4">Past Performance</h2>
            <p className="text-white/40 text-sm md:text-base max-w-xl mx-auto">
              Consistent returns through disciplined strategies and risk management.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
            {/* Monthly Returns */}
            <div 
              className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6"
              style={{
                opacity: isVisible.performance ? 1 : 0,
                transform: isVisible.performance ? 'translateX(0)' : 'translateX(-30px)',
                transition: 'all 0.7s ease-out 200ms'
              }}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-white font-medium mb-1">Monthly Returns</h3>
                  <p className="text-white/40 text-xs">Past 7 months performance</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-light text-green-400">+5.04%</p>
                  <p className="text-white/40 text-xs">Avg. Monthly</p>
                </div>
              </div>
              
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyPerformance}>
                    <XAxis 
                      dataKey="month" 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                      dataKey="return" 
                      fill="#f59e0b" 
                      radius={[4, 4, 0, 0]}
                      opacity={0.8}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Cumulative Growth */}
            <div 
              className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6"
              style={{
                opacity: isVisible.performance ? 1 : 0,
                transform: isVisible.performance ? 'translateX(0)' : 'translateX(30px)',
                transition: 'all 0.7s ease-out 300ms'
              }}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-white font-medium mb-1">Portfolio Growth</h3>
                  <p className="text-white/40 text-xs">$100,000 starting capital</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-light text-green-400">$141,084</p>
                  <p className="text-white/40 text-xs">+41.08% Total</p>
                </div>
              </div>
              
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={cumulativeGrowth}>
                    <defs>
                      <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="month" 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                      tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`}
                      domain={['dataMin - 5000', 'dataMax + 5000']}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#22c55e" 
                      strokeWidth={2}
                      fill="url(#growthGradient)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <p 
            className="text-white/25 text-[10px] md:text-xs text-center mt-6 max-w-2xl mx-auto"
            style={{
              opacity: isVisible.performance ? 1 : 0,
              transition: 'all 0.7s ease-out 500ms'
            }}
          >
            *Past performance does not guarantee future results. Trading involves risk. Only invest what you can afford to lose.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        ref={el => sectionRefs.current.cta = el}
        className="py-16 md:py-24 bg-gradient-to-t from-amber-500/[0.03] to-transparent"
      >
        <div className="max-w-[800px] mx-auto px-4 md:px-8 text-center">
          <div
            style={{
              opacity: isVisible.cta ? 1 : 0,
              transform: isVisible.cta ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.7s ease-out'
            }}
          >
            <h2 className="text-2xl md:text-4xl font-light text-white mb-4">
              Ready to Start Growing?
            </h2>
            <p className="text-white/40 text-sm md:text-base mb-8 max-w-lg mx-auto">
              Join hundreds of investors who trust Coin Bills with their portfolio management. 
              Schedule a demo to see our platform in action.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/"
                className="px-8 py-3.5 bg-amber-500 text-black font-medium text-sm tracking-wider rounded-sm hover:bg-amber-400 transition-colors flex items-center justify-center gap-2"
              >
                SCHEDULE DEMO
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link 
                to="/"
                className="px-8 py-3.5 border border-white/20 text-white/80 font-medium text-sm tracking-wider rounded-sm hover:border-white/40 transition-colors"
              >
                BACK TO HOME
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorksPage;
